// Input (drawing v2.6, screen 2, row Input; reference section 7): 44 tall at
// every width, 16px text (stops a phone zooming), a 1px `--control` border.
// Hover darkens the border; focus is the ring; error is a 2px `--bad` border
// with the message under it and an icon; disabled is the segmented fill;
// read-only is the raised fill with no border. A label on every field.

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { AlertTriangle } from "lucide-react";
import { cx } from "./cx";

interface FieldChrome {
  label: ReactNode;
  optional?: boolean | undefined;
  hint?: ReactNode | undefined;
  error?: ReactNode | undefined;
  icon?: ReactNode | undefined;
  className?: string | undefined;
}

export function Field({ label, optional, hint, error, htmlFor, children, className }: FieldChrome & { htmlFor?: string | undefined; children: ReactNode }) {
  return (
    <div className={cx("pg-field", className)}>
      <label className="pg-field__label" htmlFor={htmlFor}>
        {label}
        {optional && <small className="pg-field__opt">optional</small>}
      </label>
      {children}
      {error ? (
        <span className="pg-errt" role="alert">
          <AlertTriangle className="pg-ico pg-ico--s" aria-hidden />
          {error}
        </span>
      ) : (
        hint && <span className="pg-hint">{hint}</span>
      )}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "className">, FieldChrome {}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, optional, hint, error, icon, className, id, disabled, readOnly, ...rest }, ref) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} optional={optional} hint={hint} error={error} htmlFor={inputId} className={className}>
      <div className={cx("pg-inp", Boolean(error) && "is-error", disabled && "is-disabled", readOnly && "is-readonly")}>
        <input ref={ref} id={inputId} disabled={disabled} readOnly={readOnly} aria-invalid={error ? true : undefined} {...rest} />
        {icon}
      </div>
    </Field>
  );
});

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className">, FieldChrome {}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea({ label, optional, hint, error, className, id, disabled, readOnly, ...rest }, ref) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} optional={optional} hint={hint} error={error} htmlFor={inputId} className={className}>
      <div className={cx("pg-inp pg-inp--area", Boolean(error) && "is-error", disabled && "is-disabled", readOnly && "is-readonly")}>
        <textarea ref={ref} id={inputId} disabled={disabled} readOnly={readOnly} aria-invalid={error ? true : undefined} {...rest} />
      </div>
    </Field>
  );
});
